import { forwardRef, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { isNullish } from '@sas/nova-core/typeguards';
import { EarconName, useEarcon } from '../../hooks/useEarcon';
import { useFormatter } from '../../locale/formatting';
import { useFormatLocale } from '../../locale/formatting/useFormatLocale';
import { toLatinNumberingSystemLocale } from '../../locale/localeUtils';
import { renderDesignTokens, useDesignTokens } from '../../theme/theme';
import { setRef } from '../../util/ref-util';
import { postfixTestId } from '../../util/testId';
import { ButtonVariant, getButtonDesignTokens } from '../Button';
import { useDensity } from '../Density';
import { checkValidNumberFormat, getNumberSeparators, parseAndFormatInput, validateNumericInput } from '../Input';
import { getInputDesignTokens } from '../Input/__internal__/BaseInput/BaseInput';
import { NumericStepperInput, SpinButtons } from './__internal__';
import { isEqual, isInteger, isLarger, isLess, isNotANumber, round, validateRangeInputE } from './util';
import './NumericStepper.css';
import { jsx as _jsx } from "react/jsx-runtime";
const DEFAULT_MINIMUM_FRACTION_DIGITS = 0;
const DEFAULT_MAXIMUM_FRACTION_DIGITS = 3;

/**
 * The minimum value allowed.
 */
export const MINIMUM_VALUE = -999_999_999;
/**
 * The maximum value allowed.
 */
export const MAXIMUM_VALUE = 999_999_999;
/**
 * Minimum number of decimal points.
 */
export const MINIMUM_FRACTION_DIGITS = 0;
/**
 * Maximum number of decimal points.
 */
export const MAXIMUM_FRACTION_DIGITS = 100;
export const NumericStepper = /*#__PURE__*/forwardRef(({
  allowIntegerOnly = false,
  enableRevertToCommitted = false,
  disabled = false,
  editMode = 'select',
  readOnly = false,
  required = false,
  maximum = MAXIMUM_VALUE,
  minimum = MINIMUM_VALUE,
  nextValueOnInvalid = minimum,
  stepSize = 1,
  pageSize,
  // it can't be set to minimum here when it is undefined,
  // because consumer can't get informed for the change.
  value /* = minimum */,
  status: customizedStatus,
  message: customizedMessage,
  onChange,
  onCommit,
  className,
  tooltip,
  testId,
  ...props
}, ref) => {
  const numberFormatOptions = useMemo(() => {
    const options = {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    };
    if (!allowIntegerOnly) {
      options.minimumFractionDigits = props.numberFormatOptions?.minimumFractionDigits ?? DEFAULT_MINIMUM_FRACTION_DIGITS;
      options.maximumFractionDigits = props.numberFormatOptions?.maximumFractionDigits ?? Math.max(DEFAULT_MAXIMUM_FRACTION_DIGITS, options.minimumFractionDigits);
    }
    return options;
  }, [allowIntegerOnly, props.numberFormatOptions?.maximumFractionDigits, props.numberFormatOptions?.minimumFractionDigits]);
  const maxFractionDigits = numberFormatOptions.maximumFractionDigits;
  assertNumberFormatOptionsValidity(numberFormatOptions);
  assertMinMaxValidity(minimum, maximum, maxFractionDigits);
  assertStepSizeValidity(stepSize, pageSize, maxFractionDigits);
  assertNextValueOnInvalidValidity(nextValueOnInvalid, minimum, maximum, maxFractionDigits);
  value = typeof value === 'number' ? normalize(value, numberFormatOptions) : value;
  const inputRef = useRef(null);
  // think of this state should not rerender the ui, then useRef instead of useState.
  const isLiveRef = useRef(false);

  // references for commit value
  const lastCommitInfoRef = useRef();
  // when enable revert to commit, and an invalid value detected, this value will be emitted.
  const lastSuccessCommitValueRef = useRef(minimum);
  // the display value in last onChange event
  const lastChangedValueRef = useRef();
  // indicate if a blank value is from user set directly (unexpected), not from onChange event (expected).
  const isUnexpectedBlankValueRef = useRef(false);
  const locale = useFormatLocale();
  const [valueState, setValueState] = useState({
    status: undefined,
    message: ''
  });
  const maxLength = useMemo(() => {
    const options = {
      minimumFractionDigits: numberFormatOptions.maximumFractionDigits,
      maximumFractionDigits: numberFormatOptions.maximumFractionDigits
    };
    return Math.max(parseAndFormatInput(minimum.toString(), undefined, options).formatValue.length, parseAndFormatInput(maximum.toString(), undefined, options).formatValue.length);
  }, [minimum, maximum, numberFormatOptions.maximumFractionDigits]);
  const fireValueChanged = useCallback((displayValue, roundedRawValue, reason) => {
    if (lastChangedValueRef.current !== displayValue) {
      lastChangedValueRef.current = displayValue;
      const numericValue = typeof roundedRawValue === 'number' ? roundedRawValue : Number(roundedRawValue);
      onChange({
        displayValue,
        numericValue,
        strNumericValue: roundedRawValue.toString(),
        reason
      });
    }
  }, [onChange]);
  const fireValueCommitted = useCallback((value, reason) => {
    const errorType = value === null && lastChangedValueRef.current !== undefined ? getErrorType({
      invalidValue: lastChangedValueRef.current,
      min: minimum,
      max: maximum,
      allowIntegerOnly,
      locale
    }) : undefined;
    if (lastCommitInfoRef.current === undefined || !isEqual(lastCommitInfoRef.current.strValue, value) || lastCommitInfoRef.current.reason !== reason || lastCommitInfoRef.current.error !== errorType) {
      const [numValue, strValue] = typeof value === 'number' ? [value, value.toString()] : typeof value === 'string' ? [Number(value), value] : [value, value];
      lastCommitInfoRef.current = {
        value: numValue,
        strValue,
        reason,
        error: errorType
      };
      if (!isNullish(value)) {
        lastSuccessCommitValueRef.current = value;
      }
      onCommit?.({
        value: numValue,
        strValue,
        reason,
        error: errorType
      });
    }
  }, [allowIntegerOnly, locale, maximum, minimum, onCommit]);
  const onSpinButtonsValueChange = e => {
    const localeStringValue = Intl.NumberFormat(toLatinNumberingSystemLocale(locale), numberFormatOptions).format(e.newValue);
    const {
      formatValue,
      roundedRawValue
    } = parseValue(localeStringValue, locale, numberFormatOptions);
    fireValueChanged(formatValue, roundedRawValue, 'press');
  };
  const onSpinButtonsValueCommit = e => {
    isLiveRef.current = false;
    if (e.reason === 'blur') {
      // This is requested in NOVACOMMONS-4457
      fireValueCommitted(isNotANumber(roundedRawValue) ? null : roundedRawValue, e.reason);
    } else {
      fireValueCommitted(e.value, e.reason);
    }
  };
  const earconInputFull = useEarcon({
    source: EarconName.InputFull
  });
  const earconError = useEarcon({
    source: EarconName.Error
  });
  const formatter = useFormatter();
  const onInputStateChange = e => {
    switch (e.state) {
      case 'change':
        {
          isLiveRef.current = true;
          const {
            strRawValue
          } = parseValue(e.value, locale, numberFormatOptions);
          const validDecimalValue = getValidDecimalValue(e.value, locale, numberFormatOptions);
          const hasMoreDecimal = validDecimalValue !== e.value;
          if (hasMoreDecimal) {
            earconInputFull.playFromStart();
            const rawValidDecimalValue = parseValue(validDecimalValue, locale, numberFormatOptions).strRawValue;
            fireValueChanged(validDecimalValue, rawValidDecimalValue, 'type');
          } else {
            fireValueChanged(e.value, strRawValue, 'type');
          }
          break;
        }
      case 'enter':
      case 'blur':
        isLiveRef.current = false;
        const value = e.value;
        const {
          status,
          message
        } = getFinalStatusAndMessage(value, isLiveRef.current, locale, required, allowIntegerOnly, minimum, maximum, customizedStatus, customizedMessage, formatter);
        if (e.state === 'blur' && (value === '' || status !== undefined) && enableRevertToCommitted) {
          const lastSuccessCommitFormatValue = parseValue(lastSuccessCommitValueRef.current, locale, numberFormatOptions).formatValue;

          // invoke valueChange internally
          fireValueChanged(lastSuccessCommitFormatValue, lastSuccessCommitValueRef.current, 'revise');
          fireValueCommitted(lastSuccessCommitValueRef.current, 'revise');
        } else {
          const {
            formatValue,
            roundedRawValue
          } = parseValue(value, locale, numberFormatOptions, isLiveRef.current);

          // onChange
          if (status === undefined && lastChangedValueRef.current !== undefined) {
            // invoke valueChange internally
            fireValueChanged(value === '' ? '' : formatValue, roundedRawValue, e.state);
          } else {
            setValueState({
              status,
              message
            });
          }

          // onCommit
          fireValueCommitted(status === undefined ? value !== '' ? roundedRawValue : undefined : null, e.state);
        }
        break;
      case 'focus':
        {
          isLiveRef.current = true;
          const {
            roundedRawValue
          } = parseValue(e.value, locale, numberFormatOptions);

          // show raw number to edit in edit mode of 'combo'
          if (editMode === 'combo' && valueState.status === undefined && !isNotANumber(roundedRawValue) && e.value.toString() !== roundedRawValue.toString()) {
            const localeStringValue = Intl.NumberFormat(toLatinNumberingSystemLocale(locale), {
              useGrouping: false,
              ...numberFormatOptions
            }).format(roundedRawValue);

            // invoke ValueChange internally
            fireValueChanged(localeStringValue, roundedRawValue, 'focus');
          }
          break;
        }
    }
  };
  const {
    formatValue,
    strRawValue,
    roundedRawValue
  } = parseValue(value, locale, numberFormatOptions);
  const formattedNextValue = parseValue(nextValueOnInvalid, locale, numberFormatOptions).formatValue;
  useEffect(function enableEarcon() {
    const input = inputRef.current;
    const isInput = input instanceof HTMLInputElement;
    if (!isInput) {
      return;
    }
    const handleKeyDown = e => {
      if (input.disabled || input.readOnly) {
        return;
      }
      if (isPrintableKey(e)) {
        if (input && input.value.length === input.maxLength && (input.selectionEnd ?? 0) - (input.selectionStart ?? 0) === 0) {
          earconInputFull.playFromStart();
          return;
        } else if (isNumberIrrelevantKey(e.key, locale)) {
          earconError.playFromStart();
        }
      }
    };
    input.addEventListener('keydown', handleKeyDown);
    return () => {
      if (!isInput) {
        return;
      }
      input.removeEventListener('keydown', handleKeyDown);
    };
  });
  useEffect(
  // An commit event will be emitted if an invalid value becomes valid due to one of below properties updated:
  // minimum, maximum, customizedStatus, required or allowIntegerOnly.
  function appendCommit() {
    if (isLiveRef.current) return;
    const {
      status
    } = getFinalStatusAndMessage(value, isLiveRef.current, locale, required, allowIntegerOnly, minimum, maximum, customizedStatus, customizedMessage);
    const errorIsGone = status === undefined && status !== valueState.status;
    const isValidValue = !isNotANumber(roundedRawValue);
    const valueIsNotCommitted = lastSuccessCommitValueRef.current !== roundedRawValue;
    if (errorIsGone && isValidValue && valueIsNotCommitted) {
      fireValueCommitted(roundedRawValue, 'revise');
    }
  },
  // eslint-disable-next-line react-hooks/exhaustive-deps
  [minimum, maximum, customizedStatus, required, allowIntegerOnly]);
  useEffect(function checkValue() {
    const {
      status,
      message
    } = getFinalStatusAndMessage(value, isLiveRef.current, locale, required, allowIntegerOnly, minimum, maximum, customizedStatus, customizedMessage);
    const isUntouched = lastCommitInfoRef.current?.value === undefined;
    if (!(value === '' && editMode === 'combo' && required && isUntouched)) {
      setValueState({
        status,
        message
      });
    }
    const isInitial = lastChangedValueRef.current === undefined;
    const isUnexpectedValue = lastChangedValueRef.current !== formatValue && lastChangedValueRef.current !== value;
    isUnexpectedBlankValueRef.current = !isInitial && isUnexpectedValue && value === '';

    // There should be always a value shown in select edit mode.
    // So if a blank string, an undefined or a Non-number value is provided
    // without from onChange event or when initializing,
    // the minimum will be supplied.
    const isInvalidValueToSelectMode = (status || value === '' || value === undefined) && (isInitial || isUnexpectedValue) && editMode === 'select';

    // When initializing a Numeric Stepper in combo edit mode with a undefined value,
    // the minimum value should be shown as well.
    const isInvalidValueToComboMode = value === undefined && isInitial && editMode === 'combo';
    if (isInvalidValueToComboMode || isInvalidValueToSelectMode) {
      // invoke valueChange internally
      fireValueChanged(formattedNextValue, nextValueOnInvalid, 'revise');
      fireValueCommitted(nextValueOnInvalid, 'revise');
      return;
    }
    if (isInitial && !status) {
      lastSuccessCommitValueRef.current = value === '' ? minimum : strRawValue;
    }

    // If a value is changed after formatting, to notice user
    const isCommitted = !isLiveRef.current;
    const isValidValue = !status;
    const hasValue = value !== '';
    const valueIsChangedDueToRound = strRawValue !== roundedRawValue;
    if (isCommitted && hasValue && isValidValue && valueIsChangedDueToRound) {
      // invoke valueChange internally to notice user
      fireValueChanged(formatValue, roundedRawValue, 'revise');
      fireValueCommitted(roundedRawValue, 'revise');
      return;
    }

    // reset last commit info and last success commit value if value is provided not from onChange event
    if (!isInitial && isUnexpectedValue) {
      lastChangedValueRef.current = formatValue;
      lastCommitInfoRef.current = undefined;
      lastSuccessCommitValueRef.current = minimum;
    }
  }, [allowIntegerOnly, customizedMessage, customizedStatus, editMode, fireValueChanged, fireValueCommitted, formatValue, formattedNextValue, locale, maximum, minimum, nextValueOnInvalid, required, roundedRawValue, strRawValue, value]);
  useEffect(function updateA11yInfo() {
    const input = inputRef.current;
    if (!input) return;
    if (isUnexpectedBlankValueRef.current) {
      input.setAttribute('aria-valuetext', 'blank');
      input.removeAttribute('aria-valuenow');
      return;
    }
    if (isNotANumber(roundedRawValue)) {
      input.setAttribute('aria-valuetext', value?.toString() ?? '');
      input.removeAttribute('aria-valuenow');
    } else {
      input.setAttribute('aria-valuenow', roundedRawValue.toString());
      input.removeAttribute('aria-valuetext');
    }

    // Add an assertion to ensure the minimum and maximum be in round.
    input.setAttribute('aria-valuemin', minimum.toString());
    input.setAttribute('aria-valuemax', maximum.toString());
  }, [value, roundedRawValue, minimum, maximum, editMode, readOnly]);
  const setInputRef = input => {
    inputRef.current = input;
    setRef(ref, input);
  };
  const dt = useDesignTokens();
  const {
    density
  } = useDensity();
  const tokens = getNumericStepperDesignTokens(dt, {
    disabled,
    status: valueState.status,
    editable: editMode === 'combo',
    variant: 'standard',
    isCustomFocusTurnedOn: true,
    density
  });
  if (readOnly) {
    return /*#__PURE__*/_jsx(NumericStepperInput, {
      ...props,
      ref: setInputRef,
      readOnly: true,
      disabled: disabled,
      maxLength: maxLength,
      value: formatValue,
      onStateChange: () => {}
    });
  } else {
    return /*#__PURE__*/_jsx(SpinButtons, {
      testId: testId,
      tooltip: tooltip,
      style: {
        ...tokens,
        ...props.style
      },
      className: className,
      disabled: disabled,
      minimum: minimum,
      maximum: maximum,
      nextValueOnInvalid: nextValueOnInvalid,
      value: isNotANumber(roundedRawValue) ? undefined : roundedRawValue,
      stepSize: stepSize,
      pageSize: pageSize,
      onValueChange: onSpinButtonsValueChange,
      onValueCommit: onSpinButtonsValueCommit,
      children: /*#__PURE__*/_jsx(NumericStepperInput, {
        ...props,
        disabled: disabled,
        editMode: editMode,
        required: required,
        maxLength: maxLength,
        value: isLiveRef.current || valueState.status || value === '' ? getLocaleStringValue(value, locale) : formatValue,
        testId: postfixTestId(testId, 'inputField'),
        status: valueState.status,
        message: valueState.message,
        ref: setInputRef,
        onStateChange: onInputStateChange
      })
    });
  }
});
function assertNumberFormatOptionsValidity(options) {
  if (options.minimumFractionDigits < MINIMUM_FRACTION_DIGITS) {
    throw new Error(`The minimum number of fraction digits cannot be less than ${MINIMUM_FRACTION_DIGITS}.`);
  }
  if (options.maximumFractionDigits > MAXIMUM_FRACTION_DIGITS) {
    throw new Error(`The maximum number of fraction digits cannot be larger than ${MAXIMUM_FRACTION_DIGITS}.`);
  }
  if (options.maximumFractionDigits < options.minimumFractionDigits) {
    throw new Error(`The maximum number of fraction digits cannot be less than the minimum number of fraction digits.`);
  }
}
function assertMinMaxValidity(min, max, maxFractionDigits) {
  if (isNotANumber(min) || isNotANumber(max)) {
    throw new Error('The minimum/maximum is invalid numeric literal');
  }
  const fractionDigits = Math.max(min.toString().split('.')[1]?.length ?? 0, max.toString().split('.')[1]?.length ?? 0);
  if (fractionDigits > maxFractionDigits) {
    throw new Error(`The number of fraction digits in minimum/maximum cannot be larger than maximum fraction digits.`);
  }
  if (isLess(min, MINIMUM_VALUE)) {
    throw new Error(`The minimum cannot be less than ${MINIMUM_VALUE}.`);
  }
  if (isLarger(max, MAXIMUM_VALUE)) {
    throw new Error(`The maximum cannot be greater than ${MAXIMUM_VALUE}.`);
  }
  if (isLess(max, min)) {
    throw new Error(`The maximum cannot be less than minimum.`);
  }
}
function assertStepSizeValidity(stepSize, pageSize, maxFractionDigits) {
  if (isNotANumber(stepSize) || pageSize !== undefined && isNotANumber(pageSize)) {
    throw new Error('stepSize/pageSize is invalid numeric literal');
  }
  const fractionDigits = Math.max(stepSize.toString().split('.')[1]?.length ?? 0, pageSize?.toString().split('.')[1]?.length ?? 0, 0);
  if (fractionDigits > maxFractionDigits) {
    throw new Error(`The number of fraction digits in stepSize/pageSize cannot be larger than maximum fraction digits.`);
  }
  if (!isLarger(stepSize, 0)) {
    throw new Error(`The stepSize cannot be less than or equals 0.`);
  }
  if (pageSize !== undefined && !isLarger(pageSize, 0)) {
    throw new Error(`The pageSize cannot be less than or equals 0.`);
  }
  if (pageSize !== undefined && isLess(pageSize, stepSize)) {
    throw new Error(`The pageSize cannot be less than stepSize.`);
  }
}
function assertNextValueOnInvalidValidity(nextValueOnInvalid, min, max, maxFractionDigits) {
  if (nextValueOnInvalid === undefined) {
    return;
  } else if (isNotANumber(nextValueOnInvalid)) {
    throw new Error(`The nextValueOnInvalid is an invalid numeric literal.`);
  }
  const fractionDigits = nextValueOnInvalid.toString().split('.')[1]?.length ?? 0;
  if (fractionDigits > maxFractionDigits) {
    throw new Error(`The number of fraction digits in nextValueOnInvalid cannot be larger than maximum fraction digits.`);
  }
  if (isLess(nextValueOnInvalid, min)) {
    throw new Error(`The nextValueOnInvalid cannot be less than minimum value.`);
  }
  if (isLarger(nextValueOnInvalid, max)) {
    throw new Error(`The nextValueOnInvalid cannot be larger than maximum value.`);
  }
}
function validate(value, isLive, locale, isRequired, allowIntegerOnly, minimum, maximum, _formatter) {
  const validateProps = {
    value: (value ?? '').toLocaleString(locale),
    isRequired,
    allowIntegersOnly: allowIntegerOnly,
    isLive,
    appLocale: locale,
    minValue: minimum,
    maxValue: maximum,
    inclusiveMode: 3
  };
  return isLive ? validateNumericInput(validateProps.value, validateProps.appLocale, validateProps.isRequired, validateProps.allowIntegersOnly, validateProps.isLive) : validateRangeInputE(validateProps);
}
function getFinalStatusAndMessage(value, isLive, locale, isRequired, allowIntegerOnly, minimum, maximum, customizedStatus, customizedMessage, formatter) {
  const localeStringValue = getLocaleStringValue(value, locale);
  const validateState = validate(localeStringValue, isLive, locale, isRequired, allowIntegerOnly, minimum, maximum, formatter);

  // consumer status has higher priority
  if (!customizedStatus) {
    return {
      status: validateState.status,
      message: validateState.message
    };
  } else if (customizedStatus === 'alert' || customizedStatus === 'warning' && validateState.status !== 'alert') {
    return {
      status: customizedStatus,
      message: customizedMessage
    };
  } else {
    return {
      status: validateState.status,
      message: validateState.message
    };
  }
}
function getLocaleStringValue(value, locale = 'en-US', options) {
  return value === undefined ? '' : typeof value === 'string' // if value is string, it is assumed locale string already.
  ? value : Intl.NumberFormat(toLatinNumberingSystemLocale(locale), options).format(value);
}
function parseValue(value, locale = 'en-US', options, isLive) {
  const {
    formatValue,
    strRawValue
  } = parseAndFormatInput(getLocaleStringValue(value, locale, options), locale, options, undefined, isLive);
  const roundedRawValue = round(strRawValue, options.maximumFractionDigits);
  return {
    formatValue: formatValue.replace(/\u200e/, ''),
    strRawValue,
    roundedRawValue
  };
}
function getErrorType({
  invalidValue,
  allowIntegerOnly,
  min,
  max,
  locale
}) {
  if (typeof invalidValue === 'string' && (isNaN(Number(invalidValue)) || invalidValue.trim() === '' || !checkValidNumberFormat(invalidValue, locale, false))) {
    return 'notNumber';
  }
  const value = invalidValue;
  if (isLarger(value, max)) {
    return 'greaterThanMax';
  } else if (isLess(value, min)) {
    return 'lessThanMin';
  } else if (allowIntegerOnly && !isInteger(value)) {
    return 'notInteger';
  }
  return undefined;
}
function getValidDecimalValue(value, locale = 'en-US', options) {
  const isValidNumber = checkValidNumberFormat(value, locale);
  if (!isValidNumber) {
    return value;
  }
  const [decimalSeparator] = getNumberSeparators(locale);
  const decimalSeparatorPosition = value.indexOf(decimalSeparator);
  return decimalSeparatorPosition > -1 ? value.substring(0, decimalSeparatorPosition) + value.substring(decimalSeparatorPosition, decimalSeparatorPosition + (options?.maximumFractionDigits ?? MAXIMUM_FRACTION_DIGITS) + 1) : value;
}
function isPrintableKey(e) {
  if (e.ctrlKey || e.altKey || e.metaKey) {
    return false;
  }

  // during IME composition, think of it as printable
  if (e.keyCode === 229 || e.key.length === 1) {
    return true;
  }
  return false;
}
function isNumberIrrelevantKey(key, locale) {
  const [decimalSeparator, g] = getNumberSeparators(locale);
  const groupingSeparator = g === '\\s' ? ' ' : g;
  const numberRelatedKeys = `0123456789+-${decimalSeparator}${groupingSeparator}`;
  return numberRelatedKeys.indexOf(key) === -1;
}
function normalize(value, options) {
  // the maximumFractionDigits in toLocalString is [0,20]
  return parseFloat(value.toLocaleString('en-US', {
    ...options,
    useGrouping: false
  }));
}
export const getNumericStepperDesignTokens = (dt, opts) => {
  const inputTokens = getInputDesignTokens(dt, opts);
  const buttonTokens = getButtonDesignTokens(dt, {
    variant: ButtonVariant.Icon,
    density: opts?.density
  });
  return {
    ...renderDesignTokens({
      'bd-width': `0 0 ${dt.tokens['size-border-width-s']} 0`,
      'input-bd': opts?.disabled ? inputTokens['--input-disabled-bd'] : inputTokens['--input-bd'],
      'input-bg': opts?.disabled ? inputTokens['--input-disabled-bg'] : inputTokens['--input-bg'],
      'input-focus-bd': opts?.status ? inputTokens['--input-bd'] : dt.tokens['color-action-border-bottom-control-selected'],
      'input-focus-outline': 'var(--focus-light-bg)',
      'input-focus-outline-offset': 'calc(-1 * var(--focus-width))',
      'input-inline-start-pd': dt.tokens['space-base-s'],
      'input-inline-pd': opts?.density === 'high' ? dt.tokens['space-base-xs'] : dt.tokens['space-base-s'],
      'button-icon-width': buttonTokens['--icon-width'],
      'button-inline-padding': opts?.density === 'high' ? buttonTokens['--button-spacing-xs'] : buttonTokens['--button-spacing-s'],
      'bottom-bd-anim-dur': dt.tokens['duration-300'],
      'bottom-bd-anim-tm-ease-in': dt.tokens['ease-in'],
      'bottom-bd-anim-tm-ease-out': dt.tokens['ease-out']
    }, 'numeric-stepper')
  };
};
//# sourceMappingURL=NumericStepper.js.map